import React, { useEffect, useMemo, useState } from 'react';
import { Gift, Sparkles, Users, Clock } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import { CSSHolographicCard } from '@/components/drops/CSSHolographicCard';

interface DropAnnouncementCardProps {
  bulletin: any;
  onClaim?: (collectionId: string) => void;
}

interface CollectionStats {
  total_count: number;
  claimed_count: number;
  remaining_count: number;
  claim_percentage: number;
}

interface DropRarity {
  id: string;
  collection_id: string;
  rarity: 'common' | 'uncommon' | 'rare' | 'legendary';
  count: number;
  unit_value_cents: number;
  media_url?: string | null;
  thumbnail_url?: string | null;
}

const DropAnnouncementCard: React.FC<DropAnnouncementCardProps> = ({ bulletin, onClaim }) => {
  const { user } = useAuth();
  const [stats, setStats] = useState<CollectionStats | null>(null);
  const [hasClaimed, setHasClaimed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [collectionName, setCollectionName] = useState<string>('');
  const [authorName, setAuthorName] = useState<string>('');
  const [rarities, setRarities] = useState<DropRarity[]>([]);
  const [selectedRarity, setSelectedRarity] = useState<'common' | 'uncommon' | 'rare' | 'legendary' | null>(null);

  const collectionId = bulletin.drop_collection_id;

  useEffect(() => {
    if (collectionId) {
      loadDropStats();
      checkIfClaimed();
      loadCollectionAndRarities();
    }
  }, [collectionId, user?.id]);

  const loadDropStats = async () => {
    try {
      const { data, error } = await supabase
        .rpc('get_drop_collection_stats', { p_collection_id: collectionId });

      if (error) throw error;
      if (data && data.length > 0) {
        setStats(data[0]);
      }
    } catch (error) {
      console.error('Error loading drop stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCollectionAndRarities = async () => {
    try {
      // Collection info (for title/author)
      const { data: collection } = await supabase
        .from('drop_collections')
        .select('id, name, user_id')
        .eq('id', collectionId)
        .single();

      if (collection) {
        setCollectionName(collection.name || '');
        // Fetch author profile display
        const { data: profile } = await supabase
          .from('profiles')
          .select('display_name, username')
          .eq('id', collection.user_id)
          .single();
        setAuthorName(profile?.display_name || profile?.username || '');
      }

      // Rarities (with media)
      const { data: rarityData } = await supabase
        .from('drop_rarities')
        .select('id, collection_id, rarity, count, unit_value_cents, media_url, thumbnail_url')
        .eq('collection_id', collectionId)
        .order('rarity');

      const list = (rarityData || []) as DropRarity[];
      setRarities(list);

      // Choose default selected rarity
      const previewFromBulletin: any = bulletin?.metadata?.preview_rarity || bulletin?.preview_rarity;
      const allowed: Array<DropRarity['rarity']> = ['legendary', 'rare', 'uncommon', 'common'];

      const hasMedia = (r: DropRarity | undefined) => !!(r && (r.thumbnail_url || r.media_url));

      const pickHighestWithMedia = (): DropRarity['rarity'] | null => {
        for (const r of allowed) {
          const found = list.find(x => x.rarity === r);
          if (hasMedia(found)) return r;
        }
        return list.find(x => x)?.rarity || null;
      };

      if (previewFromBulletin && allowed.includes(previewFromBulletin)) {
        setSelectedRarity(previewFromBulletin);
      } else {
        setSelectedRarity(pickHighestWithMedia());
      }
    } catch (e) {
      // Non-fatal; UI will gracefully degrade
      console.error('Failed loading collection/rarities', e);
    }
  };

  const checkIfClaimed = async () => {
    if (!user?.id) return;

    try {
      const { data, error } = await supabase
        .from('user_drop_views')
        .select('claimed')
        .eq('user_id', user.id)
        .eq('drop_collection_id', collectionId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;
      
      setHasClaimed(data?.claimed || false);
    } catch (error) {
      console.error('Error checking claim status:', error);
    }
  };

  const handleClaim = () => {
    if (onClaim && collectionId) {
      onClaim(collectionId);
    }
  };

  const isTestDrop = bulletin.content?.includes('TEST DROP') || bulletin.content?.includes('Test drop');
  const remainingCount = stats?.remaining_count || 0;
  const totalCount = stats?.total_count || 0;
  const claimedPercentage = stats?.claim_percentage || 0;

  const previewUrl = useMemo(() => {
    if (selectedRarity) {
      const r = rarities.find(x => x.rarity === selectedRarity);
      if (r?.thumbnail_url) return r.thumbnail_url;
      if (r?.media_url) return r.media_url;
    }
    // fallbacks
    if (bulletin.image_url) return bulletin.image_url;
    return '';
  }, [selectedRarity, rarities, bulletin.image_url]);

  const rarityColors = {
    common: 'bg-emerald-500/20 text-emerald-100 border-emerald-500/40',
    uncommon: 'bg-blue-500/20 text-blue-100 border-blue-500/40',
    rare: 'bg-purple-500/20 text-purple-100 border-purple-500/40',
    legendary: 'bg-orange-500/20 text-orange-100 border-orange-500/40',
  } as const;

  return (
    <Card className="relative overflow-hidden bg-gradient-to-br from-purple-500/10 via-pink-500/10 to-orange-500/10 border-2 border-purple-500/30 hover:border-purple-500/50 transition-all duration-300">
      {/* Animated gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-pink-500/5 to-orange-500/5 animate-pulse" />
      
      {/* Sparkle effect */}
      <div className="absolute top-4 right-4 text-yellow-400 animate-bounce">
        <Sparkles className="w-6 h-6" />
      </div>

      {/* Content */}
      <div className="relative p-6 space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              <Gift className="w-6 h-6 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-bold">New Drop Available!</h3>
                {isTestDrop && (
                  <Badge variant="outline" className="bg-orange-500/20 border-orange-500 text-orange-600">
                    TEST
                  </Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                Limited edition collectible
              </p>
            </div>
          </div>
        </div>

        {/* Drop Preview (Holographic) */}
        <div className="relative w-full">
          <div className="max-w-[420px] mx-auto">
            <CSSHolographicCard
              imageUrl={previewUrl}
              style="rainbow"
              intensity={0.6}
              overlayData={selectedRarity ? {
                author: authorName || 'Artist',
                title: collectionName || 'Drop',
                serial: `${Math.max(remainingCount, 0)} left`,
                rarity: selectedRarity
              } : undefined}
            />
          </div>
        </div>

        {/* Drop Content */}
        <div className="space-y-3">
          <p className="text-sm leading-relaxed whitespace-pre-wrap">
            {bulletin.content}
          </p>

          {/* Rarity badges and Stats */}
          {rarities.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {(['legendary','rare','uncommon','common'] as const)
                .map(r => rarities.find(x => x.rarity === r))
                .filter(Boolean)
                .map((r) => (
                  <button
                    key={r!.id}
                    type="button"
                    onClick={() => setSelectedRarity(r!.rarity)}
                    className={`px-3 py-1.5 rounded-full border text-xs font-medium capitalize transition ${selectedRarity === r!.rarity ? 'ring-2 ring-primary' : ''} ${rarityColors[r!.rarity]}`}
                    title={`Preview ${r!.rarity}`}
                  >
                    {r!.rarity} ×{r!.count}
                  </button>
                ))}
            </div>
          )}

          {/* Stats */}
          {!loading && stats && (
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center gap-2 p-3 bg-background/50 rounded-lg">
                <Users className="w-4 h-4 text-blue-500" />
                <div>
                  <div className="text-xs text-muted-foreground">Available</div>
                  <div className="text-sm font-bold">
                    {remainingCount} / {totalCount}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 p-3 bg-background/50 rounded-lg">
                <Clock className="w-4 h-4 text-purple-500" />
                <div>
                  <div className="text-xs text-muted-foreground">Claimed</div>
                  <div className="text-sm font-bold">{claimedPercentage.toFixed(0)}%</div>
                </div>
              </div>
            </div>
          )}

          {/* Progress Bar */}
          {!loading && stats && (
            <div className="space-y-1">
              <div className="h-2 bg-background rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-500"
                  style={{ width: `${claimedPercentage}%` }}
                />
              </div>
            </div>
          )}

          {/* Claim Button */}
          <Button
            onClick={handleClaim}
            disabled={hasClaimed || remainingCount === 0 || loading}
            className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-bold text-lg py-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
            size="lg"
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                Loading...
              </>
            ) : hasClaimed ? (
              <>
                <Gift className="w-5 h-5 mr-2" />
                Already Claimed
              </>
            ) : remainingCount === 0 ? (
              'Sold Out'
            ) : (
              <>
                <Sparkles className="w-5 h-5 mr-2" />
                Claim Your Drop
              </>
            )}
          </Button>

          {/* One per user notice */}
          {!hasClaimed && remainingCount > 0 && (
            <p className="text-xs text-center text-muted-foreground">
              One drop per user • First come, first served
            </p>
          )}
        </div>
      </div>

      {/* Corner glow effect */}
      <div className="absolute -top-20 -right-20 w-40 h-40 bg-purple-500/20 rounded-full blur-3xl" />
      <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-pink-500/20 rounded-full blur-3xl" />
    </Card>
  );
};

export default DropAnnouncementCard;
